/**
 * Routes a visitor profile or log row may thread as a `returnTo` Back target,
 * mapped to whether each route has a dynamic segment. A model is only ever
 * attached to a route that can consume one — passing a positional model to a
 * segmentless route throws "More context objects than dynamic segments", the
 * crash this guards against.
 */
const RETURN_TO_ROUTES: Record<string, { hasSegment: boolean }> = {
  'visitors.profiles.show': { hasSegment: true },
  'visitors.invites.show': { hasSegment: true },
  'visitors.entry': { hasSegment: true },
  'visitors.entries': { hasSegment: false },
  'visitors.invites': { hasSegment: false },
  'visitors.invites.index': { hasSegment: false },
  'visitors.invites.groups.index': { hasSegment: false },
  'visitors.profiles': { hasSegment: false },
};

export interface ReturnToBackLink {
  route: string;
  models: string[];
}

/**
 * Resolve a sticky `returnTo`/`returnToModel` pair into a safe Back-link
 * target, or `null` when the route is missing, not allowlisted, or requires a
 * model that wasn't supplied — the caller then falls back to its own default.
 * A segmentless route drops any supplied model, so a crafted or stale
 * `?returnTo=visitors.entries&returnToModel=x` can never reach LinkTo as a
 * positional segment.
 */
export function resolveReturnToBackLink(
  returnTo: string | null | undefined,
  returnToModel: string | null | undefined,
): ReturnToBackLink | null {
  if (!returnTo) return null;

  const config = RETURN_TO_ROUTES[returnTo];
  if (!config) return null;

  if (config.hasSegment) {
    if (!returnToModel) return null;
    return { route: returnTo, models: [returnToModel] };
  }

  return { route: returnTo, models: [] };
}
